import { AutopilotTaskDto } from '@urbanx/agx.activities.client'
import {
  AgxCollapsible,
  AgxEmptyState,
  AgxIconTask,
  AgxProgressBar,
  AgxSpacer,
  AgxTypography,
} from '@urbanx/agx.ui-components2'
import { ReactNode, useMemo } from 'react'
import {
  getStepCompletionPercentage,
  getStepsCompletionInfo,
  getTaskTypeLabel,
} from 'utils/tasks'
import TaskAssigneeSelect from './TaskAssigneeSelect'
import TaskDueDateBadge from './TaskDueDateBadge'
import TaskStepItem from './TaskStepItem'
import {
  AssigneeOption,
  AssignTaskHandler,
  mapTaskTypeToIconType,
  ToggleTaskStepsHandler,
} from './taskCollapsibleShared'

interface TaskCollapsibleProps {
  task: AutopilotTaskDto
  contractId?: string
  assigneeOptions: AssigneeOption[]
  assignTask: AssignTaskHandler
  toggleTaskSteps: ToggleTaskStepsHandler
  renderTaskContent?: (props: {
    task: AutopilotTaskDto
    children: ReactNode
  }) => ReactNode
}

const TaskCollapsible = ({
  task,
  contractId,
  assigneeOptions,
  assignTask,
  toggleTaskSteps,
  renderTaskContent,
}: TaskCollapsibleProps) => {
  if (!task.category) return null

  const { campaignId } = task
  const taskSteps = task.taskSteps ?? []
  const { allStepsCompleted, latestCompletedDate } = useMemo(
    () => getStepsCompletionInfo([task]),
    [taskSteps]
  )
  const isUnassigned = useMemo(
    () =>
      (!task.assignedTo || task.assignedTo === 'Unassigned') && !task.isPending,
    [task.assignedTo, task.isPending]
  )

  const assigneeSelectId =
    task.category?.taskType ?? `assignToUserChip-${campaignId}`

  const content = isUnassigned ? (
    <AgxSpacer
      direction="horizontal"
      fill
      contentAlignment="center"
      contentJustification="center"
    >
      <AgxEmptyState
        variant="advanced"
        glyphType="locked"
        actionButtons={[]}
        label="Assign task to unlock"
      />
    </AgxSpacer>
  ) : (
    <AgxSpacer direction="vertical" fill gap="spacing-lg">
      {taskSteps.map((step) => (
        <TaskStepItem
          key={`${step.order}-${step.name}`}
          step={step}
          allStepsCompleted={allStepsCompleted}
          campaignId={campaignId}
          contractId={contractId}
          category={task.category}
          toggleTaskSteps={toggleTaskSteps}
        />
      ))}
    </AgxSpacer>
  )

  return (
    <AgxCollapsible
      key={task.category?.taskType}
      open={!task.isCompleted}
      iconPosition="none"
      enableToggle={true}
      variant={'withoutDivider'}
      title={''}
      contentPadding={{ left: 'xl', bottom: 'xl', right: 'xl' }}
      customTitle={
        <AgxSpacer
          direction="vertical"
          fill
          gap="spacing-ml"
          padding={{ left: 'spacing-xl', right: 'spacing-xl' }}
        >
          <AgxSpacer
            direction="horizontal"
            fill
            contentAlignment="center"
            contentJustification="space-between"
          >
            <AgxSpacer
              direction="horizontal"
              gap="spacing-lg"
              contentAlignment="center"
            >
              <AgxIconTask
                type={mapTaskTypeToIconType(task.category?.taskType)}
                iconColor="black"
                height={32}
                width={32}
                bgColor="var(--bg-progress)"
              />
              <AgxTypography id={'title'} type={'heading'} size={'medium'}>
                {getTaskTypeLabel(task.category?.taskType)}
              </AgxTypography>
            </AgxSpacer>
            <AgxSpacer
              direction="horizontal"
              gap="spacing-lg"
              contentJustification="end"
              contentAlignment="center"
            >
              <div onClick={(e) => e.stopPropagation()}>
                <TaskAssigneeSelect
                  id={assigneeSelectId}
                  value={task.assignedTo}
                  onAssign={(userId) => {
                    if (!task.campaignId || !task.category?.taskType) return

                    assignTask({
                      userId,
                      campaignId,
                      contractId,
                      category: task.category,
                    })
                  }}
                  options={!allStepsCompleted ? assigneeOptions : []}
                />
              </div>
              <TaskDueDateBadge
                dueDate={(task.dueDate ?? '') as string}
                isCompleted={allStepsCompleted}
                completedDate={latestCompletedDate}
              />
            </AgxSpacer>
          </AgxSpacer>
          {task.isCompleted ? (
            <AgxTypography
              id={'txtComplete'}
              type={'caption'}
              size={'medium'}
              color="var(--text-success)"
            >
              Complete! 🎉
            </AgxTypography>
          ) : (
            <AgxProgressBar
              variant="slim"
              id="progress"
              percentageProgressPosition="left"
              mode="dark"
              progress={getStepCompletionPercentage(taskSteps)}
            />
          )}
        </AgxSpacer>
      }
      content={
        <AgxSpacer direction="horizontal" fill>
          <AgxSpacer direction="vertical" fill gap="spacing-2xl">
            {renderTaskContent
              ? renderTaskContent({ task, children: content })
              : content}
          </AgxSpacer>
        </AgxSpacer>
      }
    />
  )
}

export default TaskCollapsible
